import { css, StyleSheet } from 'aphrodite';
import { type FC, useMemo } from 'react';

import type { IRawRichTextContentState } from '../../../extViewmodels';
import { tinymceEditorPlaceholderStyles } from '../../../models/UiUtils';
import { useEmailPreviewQuery } from '../../../queries/Email/useEmailPreviewQuery';
import { Button } from '../../../web/components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../web/components-v2/Dialog';
import { THEME } from '../../../web/components-v2/Theme/theme';

export type EmailPreviewDialogProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Editor body as rich content; the server renders it (merge fields + signature) for preview. */
	content: IRawRichTextContentState;
	signatureTemplateId?: string;
	templateId?: string;
};

/**
 * Renders the server-rendered email preview HTML in a components-v2 Dialog.
 *
 * We intentionally do NOT reuse the legacy `EmailPreview` component: it's a
 * self-contained modal child (own title/close/Okay) wired to the MobX
 * `ModalChildComponentContextKey`, so embedding it here produced duplicate
 * close buttons and dead actions. Instead we call the same `useEmailPreviewQuery`
 * and render its HTML ourselves, with this Dialog owning the chrome + close.
 *
 * The HTML is rendered via the iframe's `srcDoc` with a `sandbox` attribute (no
 * `allow-scripts`), so the server-rendered content — which can include
 * user-controlled merge-field/signature HTML — cannot execute scripts against
 * the app's origin. `srcDoc` also avoids the `document.write` append bug (stacked
 * documents when the data re-emits).
 */
export const EmailPreviewDialog: FC<EmailPreviewDialogProps> = ({
	open,
	onOpenChange,
	content,
	signatureTemplateId,
	templateId,
}) => {
	const query = useEmailPreviewQuery({ content, enabled: open && !!content, signatureTemplateId, templateId });

	const srcDoc = useMemo(() => {
		if (!query.data) return '';
		return `<html><head><link href="https://fonts.googleapis.com/css?family=Open+Sans" rel="stylesheet"/><style>${tinymceEditorPlaceholderStyles}</style></head><body style="padding:12px;margin:0;font-family:'Open Sans',sans-serif;">${query.data}</body></html>`;
	}, [query.data]);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogPopup styles={[styles.popup]}>
				<DialogHeader>
					<DialogTitle>Email Preview</DialogTitle>
				</DialogHeader>
				<DialogBody>
					{query.isLoading ? (
						<div className={css(styles.loading)}>Loading preview…</div>
					) : (
						// sandbox without `allow-scripts` blocks script execution; the
						// inline <style> + external font <link> still apply.
						<iframe title='Email preview' className={css(styles.frame)} srcDoc={srcDoc} sandbox='' />
					)}
				</DialogBody>
				<DialogFooter>
					<Button variant='primary' onClick={() => onOpenChange(false)}>
						Okay
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};

const styles = StyleSheet.create({
	popup: {
		maxWidth: 640,
		width: '90vw',
	},
	loading: {
		padding: THEME.spacing.md,
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.light,
	},
	frame: {
		width: '100%',
		height: 360,
		border: `1px solid ${THEME.colors.border.main}`,
		borderRadius: THEME.common.borderRadius,
		background: THEME.colors.background.light,
	},
});
